import React from 'react';
import {
    injectIntl,
    intlShape
} from 'react-intl';
import PropTypes from 'prop-types';

import css from './index.scss';

import * as variants from '../../../button/variants';
import messages from '../../messages';

import { arrowRightIcon, contactIcon, infoIcon } from '../../../../helpers/icon-dictionary';
import Resources from '../../../../helpers/resources';
import {
    configurationValuesEnum,
    utcDateFormats
} from '../../../../helpers/enums';
import { formatUtcDate } from '../../../../helpers/utils';

import Button from '../../../button';
import SvgIcon from '../../../svg-icon/svg-icon';
import Tooltip from '../../../tooltip';

/**
 * ENUMS
 */
const { CONTACT_US_URL } = configurationValuesEnum;

/**
 * ACADEMIC YEAR SETTINGS
 * ----------------------
 * Allows user to know what academic year my school is running under for 3P Learning products
 */
class AcademicYearSettings extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            showCurrentAcademicYearTooltip: false,
            contactUrl: ''
        };
    }

    /**
     * LIFECYCLE
     */
    componentDidMount() {
        if (Resources.env) {
            Resources.env.getConfigurationItem(CONTACT_US_URL).then((updatedContactUrl) => {
                this.setState(state => ({
                    ...state,
                    contactUrl: updatedContactUrl
                }));
            });
        }
    }

    /**
     * FUNCTIONS
     */
    getCurrentAcademicYearTooltipContent = () => {
        const { intl } = this.props;

        return (
            <div>
                {intl.formatMessage(messages.currentAcademicYearDescriptionOne)} <br /> <br />
                {intl.formatMessage(messages.currentAcademicYearDescriptionTwo)} <br /> <br />
                {intl.formatMessage(messages.currentAcademicYearDescriptionThree)}
            </div>
        );
    }

    getIsSchoolLevelText = () => {
        const {
            intl,
            academicYear
        } = this.props;

        return academicYear.isSchoolLevel ?
            intl.formatMessage(messages.schoolLevel) :
            intl.formatMessage(messages.regionLevel);
    }

getAcademicYear = () => {
    const { academicYear: {
        startDate,
        endDate,
        isSchoolLevel

    } } = this.props;
    const formattedStartDate = formatUtcDate({
        type: utcDateFormats.YYYY,
        date: startDate
    });
    const formattedEndDate = formatUtcDate({
        type: utcDateFormats.YYYY,
        date: endDate
    });
    if (isSchoolLevel) {
        return formattedStartDate;
    }

    return `${formattedStartDate} - ${formattedEndDate}`;
}

    /**
     * RENDER
     */
    renderAcademicYearSettings = () => {
        const {
            academicYear,
            intl
        } = this.props;

        return (
            <div className={css.academicSetting__sub}>
                <div className={css.infoIcon}>
                    <Button
                        containerClassName={css.infoIcon__button}
                        handleOnClick={() => this.setState(state => ({
                            ...state,
                            showCurrentAcademicYearTooltip: !this.state.showCurrentAcademicYearTooltip
                        }))}
                        icon={infoIcon}
                        variant={variants.ICON_ONLY}
                    />
                    {this.state.showCurrentAcademicYearTooltip && (
                        <Tooltip
                            className={css.infoIcon_tooltip}
                            content={this.getCurrentAcademicYearTooltipContent()}
                            useDefaultStyle={false}
                        />
                    )}
                </div>
                <div className={css.academicYearSettingsContainer__title}>
                    <strong>{intl.formatMessage(messages.currentAcademicYear)}</strong>
                </div>
                <div className={css.academicYearSettingsContainer__content}>
                    <div className={css.academicSetting}>
                        <div className={css.academicSetting__box__left}>
                            <div className={css.academicSetting__title}>
                                {intl.formatMessage(messages.academicYearName)}
                            </div>
                            <div className={css.academicSetting__value}>{this.getAcademicYear()}</div>
                        </div>
                        <div className={css.seprator__vertical} />
                        <div className={css.academicSetting__box__right}>
                            <div className={css.academicSetting__title}>
                                {intl.formatMessage(messages.region)}
                            </div>
                            <div className={css.academicSetting__value}>{this.getIsSchoolLevelText()}</div>
                        </div>
                    </div>
                </div>
                <div className={css.dateSettingsContainer_content}>
                    <div className={css.dateSettings__box}>
                        <div className={css.dateSettings__title}>
                            {intl.formatMessage(messages.dateStart)}
                        </div>
                        <div className={css.dateSettings__value}>
                            {formatUtcDate({
                                type: utcDateFormats['DD-MM-YYYY'],
                                date: this.props.academicYear.startDate
                            })}
                        </div>
                    </div>
                    <div className={css.dateIcon}><SvgIcon icon={arrowRightIcon} /></div>
                    <div className={css.dateSettings__box}>
                        <div className={css.dateSettings__title}>
                            {intl.formatMessage(messages.dateEnd)}
                        </div>
                        <div className={css.dateSettings__value}>
                            {formatUtcDate({
                                type: utcDateFormats['DD-MM-YYYY'],
                                date: academicYear.endDate
                            })}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderNullAcademicYear = () => {
        const { intl } = this.props;

        return (
            <div className={css.academicSetting__sub}>
                <span>{intl.formatMessage(messages.nullAcademicYear)}</span>
            </div>
        );
    };

    render() {
        const {
            intl,
            academicYear
        } = this.props;

        return (
            <div className={css.academicYearSettings}>
                <div className={css.academicYearSettings__content}>
                    <div className={css.academicYearSettingsContainer}>
                        <div className={css.academicYearSettingsContainer__box__full}>
                            {academicYear ?
                                this.renderAcademicYearSettings() :
                                this.renderNullAcademicYear()
                            }
                            <div className={`
                                ${css.contactSettingsContainer_content}
                                ${!academicYear ? css.contactSettingsContainer__null : null}
                            `}>
                                <a
                                    className={css.contactSettings__anchor}
                                    href={this.state.contactUrl}
                                    rel={'noreferrer'}
                                    target={'_blank'}
                                >
                                    <div className={css.contactIcon}><SvgIcon icon={contactIcon} /></div>
                                    <div className={css.contactText}>
                                        {intl.formatMessage(messages.contact)}
                                        <div className={css.contactUnderline} />
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

AcademicYearSettings.propTypes = {
    /**
     * Used to manage translations
     */
    intl: intlShape.isRequired,
    /**
     * Returned value from init data call used to populate init values
     */
    academicYear: PropTypes.shape({
        endDate: PropTypes.string,
        id: PropTypes.number,
        isSchoolLevel: PropTypes.bool,
        startDate: PropTypes.string,
        year: PropTypes.number
    })
};

export default injectIntl(AcademicYearSettings);



// WEBPACK FOOTER //
// ./app/components/privacy-settings/sub-components/academic-year-settings/index.js